import { watch, onBeforeUnmount, unref, type Ref, type WatchSource } from 'vue';

interface TextareaAutosizeOptions {
    minHeight?: number;
    maxHeight?: number;
    enabled?: Ref<boolean> | boolean;
    value?: WatchSource<unknown>;
}

export default function useTextareaAutosize(
    el: Ref<HTMLTextAreaElement | null>,
    options: TextareaAutosizeOptions = {}
) {
    const { minHeight, maxHeight } = options;

    function resize() {
        const textarea = unref(el);

        if (!textarea) {
            return;
        }

        textarea.style.height = 'auto';

        let height = textarea.scrollHeight;

        if (minHeight !== undefined && height < minHeight) {
            height = minHeight;
        }

        if (maxHeight !== undefined && height > maxHeight) {
            height = maxHeight;
            textarea.style.overflowY = 'auto';
        } else {
            textarea.style.overflowY = 'hidden';
        }

        textarea.style.height = `${height}px`;
    }

    function onInput() {
        if (unref(options.enabled) === false) {
            return;
        }

        resize();
    }

    let currentEl: HTMLTextAreaElement | null = null;

    function attach(textarea: HTMLTextAreaElement | null) {
        if (currentEl) {
            currentEl.removeEventListener('input', onInput);
            currentEl.removeEventListener('focus', onInput);
            currentEl = null;
        }

        if (!textarea) {
            return;
        }

        currentEl = textarea;
        textarea.addEventListener('input', onInput);
        textarea.addEventListener('focus', onInput);

        if (unref(options.enabled) !== false) {
            resize();
        }
    }

    watch(el, attach, { immediate: true });

    if (options.enabled !== undefined) {
        watch(
            () => unref(options.enabled),
            (isEnabled) => {
                if (isEnabled) {
                    resize();
                }
            }
        );
    }

    if (options.value) {
        watch(
            options.value,
            () => {
                if (unref(options.enabled) === false) {
                    return;
                }

                resize();
            },
            { flush: 'post' }
        );
    }

    onBeforeUnmount(() => {
        attach(null);
    });

    return { resize };
}
